import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, onSnapshot, addDoc, doc, updateDoc, deleteDoc, orderBy, where, getDocs, getDoc, limit, setDoc } from 'firebase/firestore'; // Added getDoc

// --- Shared Components ---

// Modal for adding/editing log categories - Ultra-Hot Design Enhanced
const LogCategoryModal = ({ onClose, onSave, categoryToEdit, setModalMessage }) => {
    const [name, setName] = useState(categoryToEdit?.name || '');
    const [icon, setIcon] = useState(categoryToEdit?.icon || 'fas fa-info-circle');
    const [color, setColor] = useState(categoryToEdit?.color || '#4d9eff');
    const [error, setError] = useState('');

    const handleSave = () => {
        setError('');
        if (!name.trim() || !icon.trim() || !color.trim()) {
            setError('All fields are required.');
            return;
        }
        if (!color.match(/^#[0-9A-Fa-f]{6}$/)) {
            setError('Invalid hex color format. Please use #RRGGBB.');
            return;
        }
        onSave({ id: categoryToEdit?.id, name: name.trim(), icon: icon.trim(), color: color.trim() });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="group relative overflow-hidden glass-card p-8 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-500 scale-100 hover:scale-[1.02] border border-white/20 animate-slideInLeft">
                {/* Ultra-enhanced background effects */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 via-blue-600/5 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/3 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <div className="relative text-center mb-8">
                    <div className="relative mx-auto mb-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center mx-auto shadow-lg animate-float">
                            <i className="fas fa-tags text-3xl text-purple-400 animate-pulse"></i>
                        </div>
                        {/* Animated rings */}
                        <div className="absolute -inset-2 rounded-full bg-gradient-to-r from-purple-500/10 to-blue-500/10 animate-ping opacity-50"></div>
                        <div className="absolute -inset-4 rounded-full bg-gradient-to-r from-purple-500/5 to-blue-500/5 animate-ping opacity-30" style={{ animationDelay: '0.5s' }}></div>
                    </div>
                    <h3 className="text-2xl font-black gradient-text animate-slideInLeft">
                        {categoryToEdit ? 'Edit Log Category' : 'Add New Log Category'}
                        <div className="h-0.5 w-0 bg-gradient-to-r from-purple-400 to-blue-400 animate-[width-expand_1s_ease-out_0.3s_forwards] rounded-full mx-auto mt-2"></div>
                    </h3>
                </div>
                
                {error && (
                    <div className="relative group bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 animate-bounceIn">
                        <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 to-red-500/5 rounded-xl"></div>
                        <div className="relative flex items-center">
                            <i className="fas fa-exclamation-triangle text-red-400 mr-3 animate-pulse"></i>
                            <p className="text-red-400 text-center text-sm font-medium">{error}</p>
                        </div>
                    </div>
                )}

                <div className="relative space-y-6 mb-8">
                    <div className="group">
                        <label htmlFor="categoryName" className="block text-gray-300 text-sm font-bold mb-3 flex items-center">
                            <i className="fas fa-tag mr-2 text-purple-400"></i>
                            Category Name
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                id="categoryName"
                                className="w-full py-4 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-400/60 focus:bg-white/10 transition-all duration-500 hover:border-purple-400/30 focus:shadow-lg focus:shadow-purple-400/20"
                                placeholder="e.g., Player Actions"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-400/0 via-purple-400/5 to-purple-400/0 opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                        </div>
                    </div>
                    
                    <div className="group">
                        <label htmlFor="categoryIcon" className="block text-gray-300 text-sm font-bold mb-3 flex items-center">
                            <i className="fas fa-icons mr-2 text-blue-400"></i>
                            Font Awesome Icon Class
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                id="categoryIcon"
                                className="w-full py-4 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400/60 focus:bg-white/10 transition-all duration-500 hover:border-blue-400/30 focus:shadow-lg focus:shadow-blue-400/20"
                                placeholder="e.g., fas fa-user, fas fa-dollar-sign"
                                value={icon}
                                onChange={(e) => setIcon(e.target.value)}
                                required
                            />
                            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/0 via-blue-400/5 to-blue-400/0 opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                        </div>
                        <p className="text-gray-400 text-xs mt-3 flex items-center animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                            <i className="fas fa-info-circle mr-2 text-indigo-400"></i>
                            Visit <a href="https://fontawesome.com/icons" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline transition-colors duration-300">Font Awesome</a> for icons.
                        </p>
                    </div>
                    
                    <div className="group">
                        <label htmlFor="categoryColor" className="block text-gray-300 text-sm font-bold mb-3 flex items-center">
                            <i className="fas fa-palette mr-2 text-emerald-400"></i>
                            Color (Hex)
                        </label>
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <input
                                    type="color"
                                    id="categoryColor"
                                    className="h-14 w-20 border-none rounded-xl cursor-pointer bg-white/10 hover:scale-105 transition-transform duration-300 shadow-lg"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                />
                                <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-emerald-400/20 to-cyan-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 blur-sm -z-10"></div>
                            </div>
                            <div className="relative flex-1">
                                <input
                                    type="text"
                                    className="w-full py-4 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400/60 focus:bg-white/10 transition-all duration-500 hover:border-emerald-400/30 focus:shadow-lg focus:shadow-emerald-400/20"
                                    placeholder="#RRGGBB"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    required
                                />
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-emerald-400/0 via-emerald-400/5 to-emerald-400/0 opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="relative flex gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="group flex-1 py-4 px-6 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white font-bold rounded-xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 border border-white/10 hover:border-gray-400/30"
                    >
                        <div className="flex items-center justify-center">
                            <i className="fas fa-times mr-3 group-hover:rotate-90 transition-transform duration-300"></i>
                            Cancel
                        </div>
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        className="group relative overflow-hidden flex-1 py-4 px-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 font-bold rounded-xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 text-white shadow-lg hover:shadow-purple-500/30"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-white/10 to-purple-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        <div className="relative flex items-center justify-center">
                            <i className="fas fa-save mr-3 group-hover:rotate-12 transition-transform duration-300"></i>
                            {categoryToEdit ? 'Save Changes' : 'Add Category'}
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Ultra-Enhanced General Settings Tab Component ---
const GeneralSettingsTab = ({ server, db, setModalMessage }) => {
    const [name, setName] = useState(server?.name || '');
    const [ipAddress, setIpAddress] = useState(server?.ipAddress || '');
    const [region, setRegion] = useState(server?.region || '');
    const [isSaving, setIsSaving] = useState(false);

    // List of regions for the select dropdown
    const regions = [
        'North America (East)',
        'North America (West)',
        'Europe (Central)',
        'Europe (West)',
        'Asia (East)',
        'Australia'
    ];

    useEffect(() => {
        // Update local state if server prop changes
        setName(server?.name || '');
        setIpAddress(server?.ipAddress || '');
        setRegion(server?.region || '');
    }, [server]);

    const handleSaveGeneralSettings = async () => {
        if (!db || !server?.id) {
            setModalMessage("Error: Database or server ID not available.");
            return;
        }
        setIsSaving(true);
        try {
            const serverDocRef = doc(db, `servers`, server.id);
            await updateDoc(serverDocRef, {
                name: name.trim(),
                ipAddress: ipAddress.trim(),
                region: region.trim(),
                updatedAt: new Date(),
            });
            setModalMessage('Server general settings updated successfully!');
        } catch (error) {
            console.error('Error updating server settings:', error);
            setModalMessage(`Failed to update settings: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full flex flex-col border border-white/20 hover:border-blue-400/50 transition-all duration-700 animate-fadeIn">
            {/* Ultra-enhanced background effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-cyan-600/3 to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
            
            <div className="relative flex items-center mb-8">
                <div className="relative mr-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center shadow-lg animate-float">
                        <i className="fas fa-server text-2xl text-blue-400 animate-pulse"></i>
                    </div>
                    {/* Animated rings */}
                    <div className="absolute -inset-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 animate-ping opacity-50"></div>
                    <div className="absolute -inset-4 rounded-xl bg-gradient-to-br from-blue-500/5 to-cyan-500/5 animate-ping opacity-30" style={{ animationDelay: '0.5s' }}></div>
                </div>
                <div>
                    <h4 className="text-2xl font-black gradient-text animate-slideInLeft">General Server Information</h4>
                    <div className="h-0.5 w-0 bg-gradient-to-r from-blue-400 to-cyan-400 animate-[width-expand_1s_ease-out_0.3s_forwards] rounded-full mt-1"></div>
                </div>
            </div>
            
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 flex-1">
                <div className="group space-y-4 animate-slideInLeft" style={{ animationDelay: '0.1s' }}>
                    <label htmlFor="serverName" className="block text-gray-300 text-sm font-bold flex items-center">
                        <i className="fas fa-server mr-2 text-blue-400"></i>
                        Server Name
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            id="serverName"
                            className="w-full py-4 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400/60 focus:bg-white/10 transition-all duration-500 hover:border-blue-400/30 focus:shadow-lg focus:shadow-blue-400/20"
                            placeholder="My Awesome Server"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/0 via-blue-400/5 to-blue-400/0 opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                    </div>
                </div>
                
                <div className="group space-y-4 animate-slideInLeft" style={{ animationDelay: '0.2s' }}>
                    <label htmlFor="serverIp" className="block text-gray-300 text-sm font-bold flex items-center">
                        <i className="fas fa-network-wired mr-2 text-cyan-400"></i>
                        IP Address
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            id="serverIp"
                            className="w-full py-4 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400/60 focus:bg-white/10 transition-all duration-500 hover:border-cyan-400/30 focus:shadow-lg focus:shadow-cyan-400/20"
                            placeholder="192.168.1.1:30120"
                            value={ipAddress}
                            onChange={(e) => setIpAddress(e.target.value)}
                        />
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-400/0 via-cyan-400/5 to-cyan-400/0 opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                    </div>
                </div>
                
                <div className="group md:col-span-2 space-y-4 animate-slideInLeft" style={{ animationDelay: '0.3s' }}>
                    <label htmlFor="serverRegion" className="block text-gray-300 text-sm font-bold flex items-center">
                        <i className="fas fa-globe mr-2 text-emerald-400"></i>
                        Region
                    </label>
                    <div className="relative">
                        <select
                            id="serverRegion"
                            className="w-full py-4 px-4 bg-white/5 border border-white/10 rounded-xl text-white cursor-pointer focus:outline-none focus:border-emerald-400/60 focus:bg-white/10 transition-all duration-500 hover:border-emerald-400/30 appearance-none pr-12 focus:shadow-lg focus:shadow-emerald-400/20"
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                            required
                        >
                            <option value="">Select a Region</option>
                            {regions.map((r, index) => (
                                <option key={index} value={r} className="bg-gray-800 text-white">{r}</option>
                            ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                            <i className="fas fa-chevron-down text-gray-400 group-hover:text-emerald-400 transition-colors duration-300"></i>
                        </div>
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-emerald-400/0 via-emerald-400/5 to-emerald-400/0 opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                    </div>
                </div>
            </div>
            
            <div className="relative flex justify-end mt-auto">
                <button
                    onClick={handleSaveGeneralSettings}
                    className={`group relative overflow-hidden px-8 py-4 rounded-xl font-bold transition-all duration-500 transform animate-bounceIn ${
                        isSaving 
                            ? 'bg-white/10 text-gray-400 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 hover:scale-105 hover:-translate-y-1 text-white shadow-lg hover:shadow-blue-500/30'
                    }`}
                    disabled={isSaving}
                    style={{ animationDelay: '0.4s' }}
                >
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    
                    <div className="relative flex items-center">
                        {isSaving ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save mr-3 group-hover:rotate-12 transition-transform duration-300"></i>
                                Save Changes
                            </>
                        )}
                    </div>
                </button>
            </div>
        </div>
    );
};

// --- Log Categories Tab Component (extracted from ServerSettings) ---
const LogCategoriesTab = ({ server, db, setModalMessage }) => {
    const [logCategories, setLogCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [errorCategories, setErrorCategories] = useState(null);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categoryToEdit, setCategoryToEdit] = useState(null);

    useEffect(() => {
        if (!db || !server?.id) {
            setErrorCategories("Firestore database or server ID not available for categories.");
            setLoadingCategories(false);
            return;
        }
        const categoriesCollectionRef = collection(db, `servers/${server.id}/logCategories`);
        const q = query(categoriesCollectionRef, orderBy('name', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedCategories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setLogCategories(fetchedCategories);
            setLoadingCategories(false);
        }, (err) => {
            console.error("Error fetching log categories:", err);
            setErrorCategories(`Failed to load log categories: ${err.message}`);
            setModalMessage(`Failed to load log categories: ${err.message}`);
            setLoadingCategories(false);
        });
        return () => unsubscribe();
    }, [db, server?.id, setModalMessage]);

    const handleSaveCategory = useCallback(async (categoryData) => {
        if (!db || !server?.id) {
            setModalMessage("Error: Database or server ID not available.");
            return;
        }
        try {
            if (categoryData.id) {
                const categoryDocRef = doc(db, `servers/${server.id}/logCategories`, categoryData.id);
                await updateDoc(categoryDocRef, { name: categoryData.name, icon: categoryData.icon, color: categoryData.color });
                setModalMessage(`Log category "${categoryData.name}" updated successfully!`);
            } else {
                const categoriesCollectionRef = collection(db, `servers/${server.id}/logCategories`);
                await addDoc(categoriesCollectionRef, { name: categoryData.name, icon: categoryData.icon, color: categoryData.color, createdAt: new Date() });
                setModalMessage(`Log category "${categoryData.name}" added successfully!`);
            }
        } catch (error) {
            console.error("Error saving log category:", error);
            setModalMessage(`Failed to save category: ${error.message}`);
        }
    }, [db, server?.id, setModalMessage]);

    const handleDeleteCategory = useCallback(async (categoryId, categoryName) => {
        if (!db || !server?.id || !window.confirm(`Are you sure you want to delete the category "${categoryName}"?`)) {
            return;
        }
        try {
            const categoryDocRef = doc(db, `servers/${server.id}/logCategories`, categoryId);
            await deleteDoc(categoryDocRef);
            setModalMessage(`Log category "${categoryName}" deleted successfully!`);
        } catch (error) {
            console.error("Error deleting log category:", error);
            setModalMessage(`Failed to delete category: ${error.message}`);
        }
    }, [db, server?.id, setModalMessage]);

    if (loadingCategories) {
        return (
            <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full flex items-center justify-center border border-white/20 animate-fadeIn">
                {/* Multi-layered loading animation */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-blue-600/3 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative text-center">
                    {/* Floating particles */}
                    <div className="absolute -top-10 -left-10 w-4 h-4 bg-purple-400/30 rounded-full animate-float" style={{ animationDelay: '0s', animationDuration: '3s' }}></div>
                    <div className="absolute -top-6 -right-8 w-2 h-2 bg-blue-400/40 rounded-full animate-float" style={{ animationDelay: '0.5s', animationDuration: '2.5s' }}></div>
                    <div className="absolute -bottom-8 -left-6 w-3 h-3 bg-indigo-400/35 rounded-full animate-float" style={{ animationDelay: '1s', animationDuration: '2.8s' }}></div>
                    
                    {/* Enhanced loading spinner */}
                    <div className="relative mb-6">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-400/20 border-t-purple-400 mx-auto"></div>
                        <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-2 border-purple-400/10 mx-auto"></div>
                    </div>
                    
                    <div className="relative">
                        <p className="text-gray-300 text-lg font-medium animate-pulse">Loading categories...</p>
                        {/* Progress indicator */}
                        <div className="mt-4 w-32 h-1 bg-gray-700 rounded-full mx-auto overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-purple-400 to-blue-400 rounded-full animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (errorCategories) {
        return (
            <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full flex items-center justify-center border border-red-500/20 animate-fadeIn">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 via-red-600/3 to-red-600/5"></div>
                
                <div className="relative text-center">
                    {/* Error icon with rings */}
                    <div className="relative mb-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto shadow-lg animate-pulse">
                            <i className="fas fa-exclamation-triangle text-3xl text-red-400"></i>
                        </div>
                        <div className="absolute -inset-2 rounded-full bg-gradient-to-r from-red-500/10 to-pink-500/10 animate-ping opacity-50"></div>
                    </div>
                    <p className="text-red-400 text-lg font-medium">{errorCategories}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full flex flex-col border border-white/20 hover:border-purple-400/50 transition-all duration-700 animate-fadeIn">
            {/* Ultra-enhanced background effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-indigo-600/3 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
            
            <div className="relative flex items-center justify-between mb-8">
                <div className="flex items-center animate-slideInLeft">
                    <div className="relative mr-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl flex items-center justify-center shadow-lg animate-heartbeat">
                            <i className="fas fa-tags text-2xl text-purple-400 animate-pulse"></i>
                        </div>
                        {/* Animated rings */}
                        <div className="absolute -inset-2 rounded-xl bg-gradient-to-br from-purple-500/10 to-indigo-500/10 animate-ping opacity-50"></div>
                        <div className="absolute -inset-4 rounded-xl bg-gradient-to-br from-purple-500/5 to-indigo-500/5 animate-ping opacity-30" style={{ animationDelay: '0.5s' }}></div>
                    </div>
                    <div>
                        <h4 className="text-2xl font-black gradient-text">Manage Log Categories</h4>
                        <div className="h-0.5 w-0 bg-gradient-to-r from-purple-400 to-indigo-400 animate-[width-expand_1s_ease-out_0.3s_forwards] rounded-full mt-1"></div>
                    </div>
                </div>
                
                <button
                    onClick={() => { setCategoryToEdit(null); setShowCategoryModal(true); }}
                    className="group relative overflow-hidden px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 font-bold rounded-xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 text-white shadow-lg hover:shadow-purple-500/30 animate-bounceIn"
                    style={{ animationDelay: '0.2s' }}
                >
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-white/10 to-purple-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    
                    <div className="relative flex items-center">
                        <i className="fas fa-plus mr-2 group-hover:rotate-90 transition-transform duration-300"></i>
                        Add Category
                    </div>
                </button>
            </div>

            {logCategories.length === 0 ? (
                <div className="relative flex-1 flex items-center justify-center">
                    {/* Floating particles for empty state */}
                    <div className="absolute -top-8 -left-8 w-4 h-4 bg-purple-400/20 rounded-full animate-float" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
                    <div className="absolute -top-4 -right-6 w-2 h-2 bg-indigo-400/30 rounded-full animate-float" style={{ animationDelay: '1s', animationDuration: '3s' }}></div>
                    <div className="absolute -bottom-6 -left-4 w-3 h-3 bg-violet-400/25 rounded-full animate-float" style={{ animationDelay: '2s', animationDuration: '3.5s' }}></div>
                    
                    <div className="relative text-center">
                        <div className="relative mb-8">
                            <div className="w-24 h-24 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-full flex items-center justify-center mx-auto shadow-lg animate-float">
                                <i className="fas fa-tags text-4xl text-purple-400 animate-pulse"></i>
                            </div>
                            {/* Animated background rings */}
                            <div className="absolute -inset-4 rounded-full bg-gradient-to-r from-purple-500/5 to-indigo-500/5 animate-ping" style={{ animationDuration: '3s' }}></div>
                            <div className="absolute -inset-8 rounded-full bg-gradient-to-r from-purple-500/3 to-indigo-500/3 animate-ping" style={{ animationDuration: '4s', animationDelay: '0.5s' }}></div>
                        </div>
                        <h3 className="text-2xl font-black gradient-text mb-3 animate-slideInLeft">No Categories Yet</h3>
                        <p className="text-gray-300 mb-8 animate-fadeIn" style={{ animationDelay: '0.2s' }}>Create your first log category to organize server logs</p>
                        <button
                            onClick={() => { setCategoryToEdit(null); setShowCategoryModal(true); }}
                            className="group relative overflow-hidden px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 font-bold rounded-xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 text-white shadow-lg hover:shadow-purple-500/30 animate-bounceIn"
                            style={{ animationDelay: '0.4s' }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-white/10 to-purple-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                            <div className="relative flex items-center">
                                <i className="fas fa-plus mr-3 group-hover:rotate-90 transition-transform duration-300"></i>
                                Add First Category
                            </div>
                        </button>
                    </div>
                </div>
            ) : (
                <div className="relative flex-1 overflow-hidden">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 h-full overflow-y-auto custom-scrollbar pr-2">
                        {logCategories.map((category, index) => (
                            <div 
                                key={category.id} 
                                className="group relative overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 rounded-2xl p-6 hover:bg-white/10 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 hover:shadow-2xl animate-fadeIn"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                {/* Shimmer overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                
                                {/* Gradient background overlay on hover */}
                                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ backgroundColor: `${category.color}05` }}></div>
                                
                                <div className="relative flex items-center justify-between mb-6">
                                    <div className="flex items-center">
                                        <div className="relative mr-3">
                                            <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500" style={{ backgroundColor: `${category.color}20` }}>
                                                <i className={`${category.icon} text-xl animate-pulse`} style={{ color: category.color }}></i>
                                            </div>
                                            {/* Animated ring */}
                                            <div className="absolute -inset-1 rounded-xl opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300" style={{ backgroundColor: `${category.color}10` }}></div>
                                        </div>
                                        <div>
                                            <h5 className="font-bold text-white text-lg group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-indigo-400 group-hover:bg-clip-text transition-all duration-500">{category.name}</h5>
                                            <p className="text-gray-400 text-sm font-mono bg-gray-800/50 px-2 py-1 rounded-md mt-1">{category.icon}</p>
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <div className="w-6 h-6 rounded-full border-2 border-white/20 shadow-lg group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: category.color }}></div>
                                        <div className="absolute -inset-1 rounded-full opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300" style={{ backgroundColor: `${category.color}20` }}></div>
                                    </div>
                                </div>
                                
                                <div className="relative flex gap-3">
                                    <button
                                        onClick={() => { setCategoryToEdit(category); setShowCategoryModal(true); }}
                                        className="group/btn flex-1 py-3 px-4 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 hover:text-blue-300 font-bold rounded-lg transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 border border-blue-500/20 hover:border-blue-500/40"
                                    >
                                        <div className="flex items-center justify-center">
                                            <i className="fas fa-edit mr-2 group-hover/btn:rotate-12 transition-transform duration-300"></i> 
                                            Edit
                                        </div>
                                    </button>
                                    <button
                                        onClick={() => handleDeleteCategory(category.id, category.name)}
                                        className="group/btn flex-1 py-3 px-4 bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 font-bold rounded-lg transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 border border-red-500/20 hover:border-red-500/40"
                                    >
                                        <div className="flex items-center justify-center">
                                            <i className="fas fa-trash mr-2 group-hover/btn:rotate-12 transition-transform duration-300"></i> 
                                            Delete
                                        </div>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {showCategoryModal && (
                <LogCategoryModal
                    onClose={() => setShowCategoryModal(false)}
                    onSave={handleSaveCategory}
                    categoryToEdit={categoryToEdit}
                    setModalMessage={setModalMessage}
                />
            )}
        </div>
    );
};

// --- Team Management Tab Component ---
const TeamManagementTab = ({ server, currentUser, db, setModalMessage }) => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [loadingTeam, setLoadingTeam] = useState(true);
    const [errorTeam, setErrorTeam] = useState(null);
    const [searchEmail, setSearchEmail] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isAddingUser, setIsAddingUser] = useState(false);

    // Default roles (can be expanded later)
    const roles = ['viewer', 'moderator', 'admin']; // Owner is special
    const ownerRole = 'owner';

    // Fetch team members for the specific server
    useEffect(() => {
        if (!db || !server?.id) {
            setErrorTeam("Firestore database or server ID not available for team.");
            setLoadingTeam(false);
            return;
        }

        const teamCollectionRef = collection(db, `servers/${server.id}/team`);
        const unsubscribe = onSnapshot(teamCollectionRef, async (snapshot) => { // Made async to fetch owner details
            let fetchedMembers = snapshot.docs.map(doc => ({
                id: doc.id, // This will be the userId
                ...doc.data()
            }));

            // --- Ensure owner is always in the list and at the top ---
            const ownerId = server.owner;
            const ownerInTeam = fetchedMembers.find(member => member.id === ownerId);

            if (!ownerInTeam && ownerId) {
                // Fetch owner's user data if not found in team members
                try {
                    const ownerUserDocRef = doc(db, `users/${ownerId}`);
                    const ownerUserDocSnap = await getDoc(ownerUserDocRef);
                    if (ownerUserDocSnap.exists()) {
                        const ownerUserData = ownerUserDocSnap.data();
                        fetchedMembers.unshift({ // Add to the beginning of the array
                            id: ownerId,
                            email: ownerUserData.email,
                            name: ownerUserData.username || ownerUserData.email.split('@')[0],
                            role: 'owner', // Explicitly set role to owner
                            addedAt: ownerUserData.createdAt ? new Date(ownerUserData.createdAt) : new Date(),
                            isServerOwner: true // Custom flag for easy identification
                        });
                    } else {
                        console.warn("Server owner user document not found for ID:", ownerId);
                        // Fallback: Add a placeholder owner if user document not found
                         fetchedMembers.unshift({
                            id: ownerId,
                            email: `owner-${ownerId.substring(0, 5)}@unknown.com`,
                            name: `Server Owner (${ownerId.substring(0, 5)})`,
                            role: 'owner',
                            addedAt: new Date(),
                            isServerOwner: true
                        });
                    }
                } catch (err) {
                    console.error("Error fetching server owner details:", err);
                    setModalMessage(`Error fetching owner details: ${err.message}`);
                    // Fallback: Add a placeholder owner even if fetch fails
                    fetchedMembers.unshift({
                        id: ownerId,
                        email: `owner-${ownerId.substring(0, 5)}@error.com`,
                        name: `Server Owner (Error)`,
                        role: 'owner',
                        addedAt: new Date(),
                        isServerOwner: true
                    });
                }
            } else if (ownerInTeam && ownerInTeam.id === ownerId) {
                // If owner is in team, ensure they are at the top and marked as owner
                fetchedMembers = fetchedMembers.filter(member => member.id !== ownerId); // Remove current owner entry
                fetchedMembers.unshift({ ...ownerInTeam, role: 'owner', isServerOwner: true }); // Add to top with owner role
            }
            // Sort remaining members alphabetically by name/email, after owner
            fetchedMembers.sort((a, b) => {
                if (a.isServerOwner) return -1; // Owner always first
                if (b.isServerOwner) return 1; // Owner always first
                const nameA = (a.name || a.email).toLowerCase();
                const nameB = (b.name || b.email).toLowerCase();
                return nameA.localeCompare(nameB);
            });


            setTeamMembers(fetchedMembers);
            setLoadingTeam(false);
        }, (err) => {
            console.error("Error fetching team members:", err);
            setErrorTeam(`Failed to load team members: ${err.message}`);
            setModalMessage(`Failed to load team members: ${err.message}`);
            setLoadingTeam(false);
        });

        return () => unsubscribe();
    }, [db, server?.id, server?.owner, setModalMessage]); // Added server.owner to dependencies

    // Search users by email
    const handleSearchEmail = useCallback(async (email) => {
        setSearchEmail(email);
        setSelectedUser(null); // Clear selected user on new search
        if (email.length < 3) { // Require at least 3 characters for search
            setSearchResults([]);
            return;
        }
        try {
            const usersRef = collection(db, 'users');
            // Firestore search for emails starting with the input
            const q = query(usersRef, 
                            where('email', '>=', email), 
                            where('email', '<=', email + '\uf8ff'), 
                            limit(5)); // Limit search results

            const querySnapshot = await getDocs(q);
            const foundUsers = querySnapshot.docs
                .map(doc => ({ 
                    id: doc.id, 
                    email: doc.data().email, 
                    displayName: doc.data().username || doc.data().email.split('@')[0] 
                })) // Ensure displayName is available
                .filter(user => !teamMembers.some(member => member.userId === user.id)); // Filter out already added members
            
            setSearchResults(foundUsers);
        } catch (error) {
            console.error("Error searching users:", error);
            setModalMessage(`Error searching users: ${error.message}`);
        }
    }, [db, teamMembers, setModalMessage]);

    // Add selected user to the team
    const handleAddUserToTeam = useCallback(async () => {
        if (!selectedUser || !server?.id || !db) return;

        setIsAddingUser(true);
        try {
            const teamCollectionRef = collection(db, `servers/${server.id}/team`);
            await setDoc(doc(teamCollectionRef, selectedUser.id), { // Use setDoc with userId as doc ID
                userId: selectedUser.id,
                email: selectedUser.email,
                name: selectedUser.displayName || selectedUser.email.split('@')[0], 
                role: 'viewer', // Default role
                addedAt: new Date(),
            });
            setModalMessage(`${selectedUser.email} added to the team as a Viewer!`);
            setSearchEmail('');
            setSearchResults([]);
            setSelectedUser(null);
        } catch (error) {
            console.error("Error adding user to team:", error);
            setModalMessage(`Failed to add user: ${error.message}`);
        } finally {
            setIsAddingUser(false);
        }
    }, [selectedUser, server?.id, db, setModalMessage]);

    // Update team member role
    const handleUpdateMemberRole = useCallback(async (memberId, newRole, memberName) => {
        if (!db || !server?.id || !memberId || !newRole) return;
        if (memberId === server.owner) { // Prevent changing owner's role via this mechanism
            setModalMessage("Owner's role cannot be changed from here.");
            return;
        }
        if (!window.confirm(`Are you sure you want to change ${memberName}'s role to "${newRole}"?`)) {
            return;
        }
        try {
            const memberDocRef = doc(db, `servers/${server.id}/team`, memberId);
            await updateDoc(memberDocRef, { role: newRole });
            setModalMessage(`${memberName}'s role updated to "${newRole}".`);
        } catch (error) {
            console.error("Error updating member role:", error);
            setModalMessage(`Failed to update role: ${error.message}`);
        }
    }, [db, server?.id, setModalMessage]);

    // Remove team member
    const handleRemoveMember = useCallback(async (memberId, memberName) => {
        if (!db || !server?.id || !memberId) return;
        if (memberId === server.owner) { // Prevent removing owner
            setModalMessage("Cannot remove the server owner.");
            return;
        }
        if (!window.confirm(`Are you sure you want to remove ${memberName} from the team?`)) {
            return;
        }
        try {
            const memberDocRef = doc(db, `servers/${server.id}/team`, memberId);
            await deleteDoc(memberDocRef);
            setModalMessage(`${memberName} removed from the team.`);
        } catch (error) {
            console.error("Error removing member:", error);
            setModalMessage(`Failed to remove member: ${error.message}`);
        }
    }, [db, server?.id, setModalMessage]);

    const isOwner = currentUser?.uid === server?.owner;

    if (loadingTeam) {
        return (
            <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full flex items-center justify-center border border-white/20 animate-fadeIn">
                {/* Multi-layered loading animation */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 via-emerald-600/3 to-green-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative text-center">
                    {/* Floating particles */}
                    <div className="absolute -top-10 -left-10 w-4 h-4 bg-green-400/30 rounded-full animate-float" style={{ animationDelay: '0s', animationDuration: '3s' }}></div>
                    <div className="absolute -top-6 -right-8 w-2 h-2 bg-emerald-400/40 rounded-full animate-float" style={{ animationDelay: '0.5s', animationDuration: '2.5s' }}></div>
                    <div className="absolute -bottom-8 -left-6 w-3 h-3 bg-teal-400/35 rounded-full animate-float" style={{ animationDelay: '1s', animationDuration: '2.8s' }}></div>
                    
                    {/* Enhanced loading spinner */}
                    <div className="relative mb-6">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-400/20 border-t-green-400 mx-auto"></div>
                        <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-2 border-green-400/10 mx-auto"></div>
                    </div>
                    
                    <div className="relative">
                        <p className="text-gray-300 text-lg font-medium animate-pulse">Loading team members...</p>
                        {/* Progress indicator */}
                        <div className="mt-4 w-32 h-1 bg-gray-700 rounded-full mx-auto overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-green-400 to-emerald-400 rounded-full animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (errorTeam) {
        return (
            <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full flex items-center justify-center border border-red-500/20 animate-fadeIn">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 via-red-600/3 to-red-600/5"></div>
                
                <div className="relative text-center">
                    {/* Error icon with rings */}
                    <div className="relative mb-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto shadow-lg animate-pulse">
                            <i className="fas fa-exclamation-triangle text-3xl text-red-400"></i>
                        </div>
                        <div className="absolute -inset-2 rounded-full bg-gradient-to-r from-red-500/10 to-pink-500/10 animate-ping opacity-50"></div>
                    </div>
                    <p className="text-red-400 text-lg font-medium">{errorTeam}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full flex flex-col border border-white/20 hover:border-green-400/50 transition-all duration-700 animate-fadeIn">
            {/* Ultra-enhanced background effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 via-emerald-600/3 to-green-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
            
            <div className="relative flex items-center mb-8">
                <div className="relative mr-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center shadow-lg animate-heartbeat">
                        <i className="fas fa-users-cog text-2xl text-green-400 animate-pulse"></i>
                    </div>
                    {/* Animated rings */}
                    <div className="absolute -inset-2 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 animate-ping opacity-50"></div>
                    <div className="absolute -inset-4 rounded-xl bg-gradient-to-br from-green-500/5 to-emerald-500/5 animate-ping opacity-30" style={{ animationDelay: '0.5s' }}></div>
                </div>
                <div>
                    <h4 className="text-2xl font-black gradient-text animate-slideInLeft">Manage Team Members</h4>
                    <div className="h-0.5 w-0 bg-gradient-to-r from-green-400 to-emerald-400 animate-[width-expand_1s_ease-out_0.3s_forwards] rounded-full mt-1"></div>
                </div>
            </div>
            
            {isOwner && (
                <div className="group/add relative bg-white/5 border border-white/10 hover:border-green-400/30 rounded-2xl p-6 mb-8 animate-slideInLeft transition-all duration-500" style={{ animationDelay: '0.1s' }}>
                    {/* Background animation */}
                    <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 via-emerald-600/3 to-green-600/5 opacity-0 group-hover/add:opacity-100 transition-opacity duration-500"></div>
                    
                    <div className="relative flex items-center mb-6">
                        <div className="relative mr-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center shadow-lg animate-float">
                                <i className="fas fa-user-plus text-lg text-blue-400 animate-pulse"></i>
                            </div>
                            <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 animate-ping opacity-50"></div>
                        </div>
                        <div>
                            <h5 className="text-xl font-bold text-white group-hover/add:text-transparent group-hover/add:bg-gradient-to-r group-hover/add:from-green-400 group-hover/add:to-emerald-400 group-hover/add:bg-clip-text transition-all duration-500">Add New Team Member</h5>
                            <div className="h-0.5 w-0 bg-gradient-to-r from-blue-400 to-cyan-400 group-hover/add:w-full transition-all duration-700 rounded-full mt-1"></div>
                        </div>
                    </div>
                    
                    <div className="relative flex flex-col lg:flex-row gap-4 mb-4">
                        <div className="relative flex-1 group">
                            <input
                                type="email"
                                className="w-full py-4 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400/60 focus:bg-white/10 transition-all duration-500 hover:border-blue-400/30 focus:shadow-lg focus:shadow-blue-400/20"
                                placeholder="Enter user email to add..."
                                value={searchEmail}
                                onChange={(e) => handleSearchEmail(e.target.value)}
                            />
                            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/0 via-blue-400/5 to-blue-400/0 opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                            
                            {searchEmail.length >= 3 && searchResults.length > 0 && (
                                <div className="absolute z-20 w-full bg-gray-800/95 backdrop-blur-sm border border-white/20 rounded-xl shadow-2xl mt-2 max-h-48 overflow-y-auto custom-scrollbar animate-fadeIn">
                                    {searchResults.map(user => (
                                        <div
                                            key={user.id}
                                            className="group px-4 py-3 hover:bg-white/10 cursor-pointer text-white flex items-center transition-colors duration-300 transform hover:scale-[1.02]"
                                            onClick={() => {
                                                setSelectedUser(user);
                                                setSearchEmail(user.email);
                                                setSearchResults([]);
                                            }}
                                        >
                                            <div className="relative mr-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                                                    <i className="fas fa-user text-sm text-blue-400"></i>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="font-bold group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-cyan-400 group-hover:bg-clip-text transition-all duration-300">{user.displayName || user.email}</div>
                                                <div className="text-gray-400 text-xs">{user.email}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {searchEmail.length >= 3 && searchResults.length === 0 && !selectedUser && (
                                <p className="absolute text-sm text-gray-400 mt-2 ml-2 animate-fadeIn">No users found with that email.</p>
                            )}
                        </div>
                        
                        <button
                            onClick={handleAddUserToTeam}
                            className={`group relative overflow-hidden px-8 py-4 rounded-xl font-bold transition-all duration-500 transform ${
                                !selectedUser || isAddingUser 
                                    ? 'bg-white/10 text-gray-400 cursor-not-allowed' 
                                    : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 hover:scale-105 hover:-translate-y-1 text-white shadow-lg hover:shadow-green-500/30'
                            }`}
                            disabled={!selectedUser || isAddingUser}
                        >
                            {!selectedUser || isAddingUser ? null : (
                                <div className="absolute inset-0 bg-gradient-to-r from-green-400/0 via-white/10 to-green-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                            )}
                            
                            <div className="relative flex items-center">
                                {isAddingUser ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-user-plus mr-3 group-hover:rotate-12 transition-transform duration-300"></i>
                                        Add User
                                    </>
                                )}
                            </div>
                        </button>
                    </div>
                    
                    {selectedUser && (
                        <div className="relative bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 animate-bounceIn">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-cyan-600/5 rounded-xl"></div>
                            <p className="relative text-gray-300 text-sm flex items-center">
                                <i className="fas fa-check-circle text-green-400 mr-2"></i>
                                Selected: <span className="font-bold text-white ml-1">{selectedUser.displayName || selectedUser.email}</span>
                                <span className="text-blue-400 ml-2">({selectedUser.email})</span>
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div className="relative flex items-center mb-6">
                <div className="relative mr-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center shadow-lg animate-float">
                        <i className="fas fa-users text-lg text-blue-400 animate-pulse"></i>
                    </div>
                    <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 animate-ping opacity-50"></div>
                </div>
                <div>
                    <h5 className="text-xl font-bold text-white">Current Team</h5>
                    <div className="h-0.5 w-0 bg-gradient-to-r from-blue-400 to-cyan-400 animate-[width-expand_1s_ease-out_0.5s_forwards] rounded-full mt-1"></div>
                </div>
            </div>
            
            {teamMembers.length === 0 ? (
                <div className="relative flex-1 flex items-center justify-center">
                    {/* Floating particles for empty state */}
                    <div className="absolute -top-8 -left-8 w-4 h-4 bg-green-400/20 rounded-full animate-float" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
                    <div className="absolute -top-4 -right-6 w-2 h-2 bg-emerald-400/30 rounded-full animate-float" style={{ animationDelay: '1s', animationDuration: '3s' }}></div>
                    <div className="absolute -bottom-6 -left-4 w-3 h-3 bg-teal-400/25 rounded-full animate-float" style={{ animationDelay: '2s', animationDuration: '3.5s' }}></div>
                    
                    <div className="relative text-center">
                        <div className="relative mb-8">
                            <div className="w-24 h-24 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center mx-auto shadow-lg animate-float">
                                <i className="fas fa-users text-4xl text-green-400 animate-pulse"></i>
                            </div>
                            {/* Animated background rings */}
                            <div className="absolute -inset-4 rounded-full bg-gradient-to-r from-green-500/5 to-emerald-500/5 animate-ping" style={{ animationDuration: '3s' }}></div>
                            <div className="absolute -inset-8 rounded-full bg-gradient-to-r from-green-500/3 to-emerald-500/3 animate-ping" style={{ animationDuration: '4s', animationDelay: '0.5s' }}></div>
                        </div>
                        <h3 className="text-2xl font-black gradient-text mb-3 animate-slideInLeft">No Team Members</h3>
                        <p className="text-gray-300 animate-fadeIn" style={{ animationDelay: '0.2s' }}>Add team members to collaborate on server management</p>
                    </div>
                </div>
            ) : (
                <div className="relative flex-1 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full overflow-y-auto custom-scrollbar pr-2">
                        {teamMembers.map((member, index) => (
                            <div 
                                key={member.id} 
                                className="group relative overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 rounded-2xl p-6 hover:bg-white/10 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 hover:shadow-2xl animate-fadeIn"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                {/* Shimmer overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                
                                {/* Role-based gradient background */}
                                <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                                    member.isServerOwner 
                                        ? 'bg-gradient-to-br from-yellow-600/5 to-amber-600/5'
                                        : 'bg-gradient-to-br from-green-600/5 to-emerald-600/5'
                                }`}></div>
                                
                                <div className="relative flex items-center justify-between mb-6">
                                    <div className="flex items-center">
                                        <div className="relative mr-4">
                                            <div className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 ${
                                                member.isServerOwner 
                                                    ? 'bg-gradient-to-br from-yellow-500/20 to-amber-500/20' 
                                                    : 'bg-gradient-to-br from-green-500/20 to-emerald-500/20'
                                            }`}>
                                                <i className={`fas ${member.isServerOwner ? 'fa-crown' : 'fa-user'} text-2xl ${
                                                    member.isServerOwner ? 'text-yellow-400' : 'text-green-400'
                                                } animate-pulse`}></i>
                                            </div>
                                            {/* Animated ring */}
                                            <div className={`absolute -inset-1 rounded-xl opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300 ${
                                                member.isServerOwner
                                                    ? 'bg-gradient-to-br from-yellow-500/10 to-amber-500/10'
                                                    : 'bg-gradient-to-br from-green-500/10 to-emerald-500/10'
                                            }`}></div>
                                        </div>
                                        <div>
                                            <h6 className="font-bold text-white text-lg group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-green-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-500 truncate max-w-[200px]">{member.name || member.email}</h6>
                                            <p className="text-gray-400 text-sm font-mono bg-gray-800/50 px-2 py-1 rounded-md mt-1 truncate max-w-[200px]">{member.email}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="relative flex items-center justify-between">
                                    <div>
                                        {member.isServerOwner ? (
                                            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-yellow-400 border border-yellow-500/30 shadow-lg">
                                                <i className="fas fa-crown mr-2 animate-pulse"></i>
                                                Owner
                                            </span>
                                        ) : isOwner ? (
                                            <div className="relative">
                                                <select
                                                    className="px-4 py-2 bg-white/5 border border-white/10 hover:border-green-400/30 rounded-xl text-white text-sm focus:outline-none focus:border-green-400/60 focus:bg-white/10 transition-all duration-500 cursor-pointer"
                                                    value={member.role}
                                                    onChange={(e) => handleUpdateMemberRole(member.id, e.target.value, member.name || member.email)}
                                                >
                                                    {roles.map(role => (
                                                        <option key={role} value={role} className="bg-gray-800 text-white">
                                                            {role.charAt(0).toUpperCase() + role.slice(1)}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400/0 via-green-400/5 to-green-400/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                                            </div>
                                        ) : (
                                            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gray-500/20 text-gray-400 border border-gray-500/30">
                                                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {isOwner && !member.isServerOwner && (
                                        <button
                                            onClick={() => handleRemoveMember(member.id, member.name || member.email)}
                                            className="group/btn px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 font-bold rounded-lg transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 border border-red-500/20 hover:border-red-500/40 shadow-lg"
                                        >
                                            <div className="flex items-center">
                                                <i className="fas fa-times mr-1 group-hover/btn:rotate-90 transition-transform duration-300"></i> 
                                                Remove
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


// --- Main ServerSettings Component ---
export function ServerSettings({ server, currentUser, db, setModalMessage }) {
    const [activeSettingsTab, setActiveSettingsTab] = useState('general'); // Default to general settings

    // Centralized loading/error states for the whole component
    // These are now less critical as sub-components manage their own
    // but useful for initial overall load state.
    const [loadingInitial, setLoadingInitial] = useState(false); // Can be used if there's an overall settings load
    const [errorInitial, setErrorInitial] = useState(null); // Can be used for overall settings error

    if (!server) {
        return (
            <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full flex items-center justify-center border border-red-500/20 animate-fadeIn">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 via-red-600/3 to-red-600/5"></div>
                
                <div className="relative text-center">
                    {/* Error icon with animated rings */}
                    <div className="relative mb-8">
                        <div className="w-24 h-24 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto shadow-lg animate-float">
                            <i className="fas fa-exclamation-triangle text-4xl text-red-400 animate-pulse"></i>
                        </div>
                        {/* Animated background rings */}
                        <div className="absolute -inset-4 rounded-full bg-gradient-to-r from-red-500/5 to-pink-500/5 animate-ping" style={{ animationDuration: '3s' }}></div>
                        <div className="absolute -inset-8 rounded-full bg-gradient-to-r from-red-500/3 to-pink-500/3 animate-ping" style={{ animationDuration: '4s', animationDelay: '0.5s' }}></div>
                    </div>
                    <h3 className="text-2xl font-black gradient-text mb-4 animate-slideInLeft">No Server Selected</h3>
                    <p className="text-gray-300 animate-fadeIn" style={{ animationDelay: '0.2s' }}>Server data is not available or still loading</p>
                </div>
            </div>
        );
    }

    return (
        <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full flex flex-col border border-white/20 hover:border-indigo-400/50 transition-all duration-700 animate-fadeIn">
            {/* Ultra-enhanced background effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-purple-600/3 to-indigo-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
            
            {/* Ultra-Enhanced Header */}
            <div className="relative flex items-center mb-8">
                <div className="relative mr-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl flex items-center justify-center shadow-lg animate-heartbeat">
                        <i className="fas fa-sliders-h text-2xl text-indigo-400 animate-pulse"></i>
                    </div>
                    {/* Animated rings */}
                    <div className="absolute -inset-2 rounded-xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 animate-ping opacity-50"></div>
                    <div className="absolute -inset-4 rounded-xl bg-gradient-to-br from-indigo-500/5 to-purple-500/5 animate-ping opacity-30" style={{ animationDelay: '0.5s' }}></div>
                </div>
                <div>
                    <h3 className="text-3xl font-black gradient-text animate-slideInLeft">Server Settings</h3>
                    <div className="h-0.5 w-0 bg-gradient-to-r from-indigo-400 to-purple-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full mt-1"></div>
                </div>
            </div>
            
            {/* Ultra-Enhanced Navigation Tabs */}
            <div className="relative mb-8">
                <div className="relative flex flex-wrap gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm overflow-hidden">
                    {/* Background shimmer */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/3 to-transparent -translate-x-full animate-shimmer"></div>
                    
                    <button
                        onClick={() => setActiveSettingsTab('general')}
                        className={`group relative overflow-hidden px-6 py-4 rounded-xl text-sm font-bold transition-all duration-500 transform ${
                            activeSettingsTab === 'general' 
                                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg scale-105 -translate-y-1' 
                                : 'bg-transparent text-gray-400 hover:bg-white/10 hover:text-white hover:scale-105 hover:-translate-y-1'
                        } animate-slideInLeft`}
                        style={{ animationDelay: '0.1s' }}
                    >
                        {activeSettingsTab === 'general' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        )}
                        <div className="relative flex items-center">
                            <i className="fas fa-server mr-2 group-hover:rotate-12 transition-transform duration-300"></i>
                            General Settings
                        </div>
                    </button>
                    
                    <button
                        onClick={() => setActiveSettingsTab('log-categories')}
                        className={`group relative overflow-hidden px-6 py-4 rounded-xl text-sm font-bold transition-all duration-500 transform ${
                            activeSettingsTab === 'log-categories' 
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg scale-105 -translate-y-1' 
                                : 'bg-transparent text-gray-400 hover:bg-white/10 hover:text-white hover:scale-105 hover:-translate-y-1'
                        } animate-slideInLeft`}
                        style={{ animationDelay: '0.2s' }}
                    >
                        {activeSettingsTab === 'log-categories' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-400/0 via-white/10 to-purple-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        )}
                        <div className="relative flex items-center">
                            <i className="fas fa-tags mr-2 group-hover:rotate-12 transition-transform duration-300"></i>
                            Log Categories
                        </div>
                    </button>
                    
                    <button
                        onClick={() => setActiveSettingsTab('team')}
                        className={`group relative overflow-hidden px-6 py-4 rounded-xl text-sm font-bold transition-all duration-500 transform ${
                            activeSettingsTab === 'team' 
                                ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg scale-105 -translate-y-1' 
                                : 'bg-transparent text-gray-400 hover:bg-white/10 hover:text-white hover:scale-105 hover:-translate-y-1'
                        } animate-slideInLeft`}
                        style={{ animationDelay: '0.3s' }}
                    >
                        {activeSettingsTab === 'team' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-green-400/0 via-white/10 to-green-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        )}
                        <div className="relative flex items-center">
                            <i className="fas fa-users-cog mr-2 group-hover:rotate-12 transition-transform duration-300"></i>
                            Team Management
                        </div>
                    </button>
                </div>
            </div>

            {/* Ultra-Enhanced Content Container */}
            <div className="relative flex-1 overflow-hidden">
                {/* Content based on active settings tab */}
                {activeSettingsTab === 'general' && (
                    <GeneralSettingsTab server={server} db={db} setModalMessage={setModalMessage} />
                )}

                {activeSettingsTab === 'log-categories' && (
                    <LogCategoriesTab server={server} db={db} setModalMessage={setModalMessage} />
                )}

                {activeSettingsTab === 'team' && (
                    <TeamManagementTab server={server} currentUser={currentUser} db={db} setModalMessage={setModalMessage} />
                )}
            </div>
        </div>
    );
}

export default ServerSettings;